import React, { useState } from "react";
import {
  ArrowLeft,
  Plus,
  Minus,
  Shirt,
  Bath,
  BedDouble,
  Scissors
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

export default function LaundryPage() {
  const navigate = useNavigate();
  const student = JSON.parse(localStorage.getItem("student"));

  const CLOTHES = [
    { id: 1, name: "Shirt", icon: Shirt },
    { id: 2, name: "Pants", icon: Scissors },
    { id: 3, name: "Uniform", icon: Shirt },
    { id: 4, name: "Saree", icon: Bath },
    { id: 5, name: "Bedsheet", icon: BedDouble },
  ];

  const [count, setCount] = useState({});
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);

  const updateCount = (id, delta) => {
    setCount((prev) => ({
      ...prev,
      [id]: Math.max(0, (prev[id] || 0) + delta),
    }));
  };

  const totalItems = Object.values(count).reduce((a, b) => a + b, 0);

  const placeOrder = async () => {
    if (totalItems === 0) {
      alert("Please select at least one item.");
      return;
    }

    const isDemo = localStorage.getItem("demoMode") === "true";

    if (isDemo) {
      setLoading(true);
      setTimeout(() => {
        alert("Order placed successfully! (Demo)");
        navigate(`/track-order?id=demo-${Date.now()}`);
        setLoading(false);
      }, 800);
      return;
    }

    setLoading(true);

    try {
      const body = {
        usn: student.usn,
        shirts: count[1] || 0,
        pants: count[2] || 0,
        uniforms: count[3] || 0,
        towels: count[4] || 0,
        bedsheets: count[5] || 0,

        notes: notes.trim() || "No additional notes"
      };

      const res = await fetch("https://hostel-laundry-api.onrender.com/orders/new",
 {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await res.json();

      if (!res.ok) {
        alert("Order failed!");
        setLoading(false);
        return;
      }

      const orderId = data.orderId;

      // Update wash count
      await fetch("https://hostel-laundry-api.onrender.com/student/washes/update",
 {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          usn: student.usn,
          added: 0
        }),
      });

      alert("Order placed successfully!");
      navigate(`/track-order?id=${orderId}`);

    } catch (err) {
      console.error(err);
      alert("Server not reachable!");
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-white p-4 pb-24 flex justify-center">
      <div className="w-full max-w-3xl space-y-5">

        {/* HEADER */}
        <header className="flex items-center justify-between bg-white/80 backdrop-blur-lg border border-neutral-200 rounded-xl px-4 py-3">
          <Link
            to="/student-dashboard"
            className="p-2 rounded-full hover:bg-neutral-100 transition"
          >
            <ArrowLeft className="w-5 h-5 text-neutral-700" />
          </Link>

          <h1 className="text-base font-semibold text-neutral-900">
            Place Laundry Order
          </h1>

          <div className="w-5" />
        </header>

        {/* CLOTHES LIST */}
        <div className="space-y-3">
          {CLOTHES.map((item) => {
            const Icon = item.icon;

            return (
              <div
                key={item.id}
                className="bg-white border border-neutral-200 rounded-2xl p-4 flex items-center justify-between hover:border-neutral-300 transition"
              >
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 bg-neutral-100 rounded-xl flex items-center justify-center">
                    <Icon className="w-5 h-5 text-neutral-700" />
                  </div>
                  <p className="text-base font-semibold text-neutral-900">
                    {item.name}
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    onClick={() => updateCount(item.id, -1)}
                    className="p-2 rounded-lg bg-neutral-100 hover:bg-neutral-200 transition"
                  >
                    <Minus className="w-4 h-4 text-neutral-700" />
                  </button>

                  <p className="w-6 text-center font-semibold text-neutral-900 text-sm">
                    {count[item.id] || 0}
                  </p>

                  <button
                    onClick={() => updateCount(item.id, 1)}
                    className="p-2 rounded-lg bg-neutral-900 text-white hover:bg-neutral-800 transition"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* SUMMARY CARD */}
        <div className="bg-white border border-neutral-200 rounded-2xl p-6">
          <h2 className="text-lg font-semibold text-neutral-900 mb-4">
            Order Summary
          </h2>

          {totalItems === 0 ? (
            <p className="text-sm text-neutral-400">No items selected.</p>
          ) : (
            <ul className="text-sm text-neutral-600 space-y-1">
              {CLOTHES.map((c) =>
                count[c.id] ? (
                  <li key={c.id}>
                    {c.name}: <b className="text-neutral-900">{count[c.id]}</b>
                  </li>
                ) : null
              )}
            </ul>
          )}

          {/* NOTES INPUT */}
          <div className="mt-5">
            <label className="text-sm font-medium text-neutral-700">
              Additional Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any specific instructions about your clothes..."
              className="mt-2 w-full min-h-[80px] p-3 rounded-xl border border-neutral-200 bg-neutral-50 text-neutral-900 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:bg-white transition-all"
            ></textarea>
          </div>

          {/* Total */}
          <div className="flex justify-between mt-5 pt-4 border-t border-neutral-100">
            <p className="font-medium text-neutral-600 text-sm">Total Items</p>
            <p className="font-bold text-neutral-900">{totalItems}</p>
          </div>

          <button
            disabled={loading}
            onClick={placeOrder}
            className="mt-5 w-full py-3.5 bg-neutral-900 text-white rounded-xl font-semibold text-sm hover:bg-neutral-800 transition-all disabled:bg-neutral-300 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
          >
            {loading ? "Placing Order..." : "Place Order"}
          </button>
        </div>

      </div>
    </div>
  );
}
